import { RefObject, useEffect } from "react";

export type KeyboardEventPredicate = (e: KeyboardEvent) => boolean;

type EventType = Pick<WindowEventMap, "keydown" | "keypress" | "keyup">;
type Options<K extends keyof EventType = "keydown"> = {
  cleanup: () => void;
  preventDefault: boolean;
  stopPropagation: boolean;
  target: RefObject<HTMLElement | null>;
  type: K;
};

export function useKeyboard<K extends keyof EventType = "keydown">(
  predicate: KeyboardEventPredicate,
  callback: () => void,
  options?: Partial<Options<K>>,
) {
  useEffect(() => {
    const controller = new AbortController();
    const type = options?.type ?? "keydown";
    const preventDefault = options?.preventDefault ?? true;
    const stopPropagation = options?.stopPropagation ?? false;

    const target = options?.target?.current ?? window;
    target.addEventListener(
      type,
      (e) => {
        if (predicate(e as KeyboardEvent)) {
          if (preventDefault) {
            e.preventDefault();
          }

          if (stopPropagation) {
            e.stopPropagation();
          }

          callback();
        }
      },
      { signal: controller.signal },
    );

    return () => {
      controller.abort();
      options?.cleanup?.();
    };
  }, [callback, predicate, options]);
}
